import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import TopBar from "../TopBar";
import Sidebar from "../Sidebar";
import { getProfile } from "../../store/actions/profileActions";
import {
  IMPERSONATOR_TOKEN_KEY,
  returnToSuperAdmin,
} from "../../store/actions/adminActions";
import PageTransitionProvider from "./PageTransitionProvider";
import ConfirmProvider from "./ConfirmProvider";
import ToastProvider from "./ToastProvider";

/**
 * Persistent app chrome — TopBar + Sidebar stay mounted across route changes
 * so only the main content area swaps (eliminates navigation flicker).
 */
export default function AuthenticatedLayout({ children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const profile = useSelector((state) => state.profile.profile);
  const [returning, setReturning] = useState(false);
  const isImpersonating = Boolean(sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY));
  const displayName =
    `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
    profile?.email ||
    t("adminManagement.users.impersonation.currentUser");

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  const handleReturnToSuperAdmin = async () => {
    setReturning(true);
    try {
      const result = await dispatch(returnToSuperAdmin());
      if (!result?.redirected) {
        history.replace("/admin");
      }
    } catch {
      setReturning(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-screen bg-gray-50 overflow-hidden">
      <div className="flex-shrink-0">
        <TopBar />
      </div>
      {isImpersonating && (
        <div className="flex flex-shrink-0 items-center justify-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950">
          <ShieldCheck size={16} className="flex-shrink-0" aria-hidden="true" />
          <span className="truncate">
            {t("adminManagement.users.impersonation.activeAs", { name: displayName })}
          </span>
          <button
            type="button"
            onClick={handleReturnToSuperAdmin}
            disabled={returning}
            className="flex-shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1 font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-wait disabled:opacity-60"
          >
            {returning
              ? t("adminManagement.users.impersonation.returning")
              : t("adminManagement.users.impersonation.returnToAdmin")}
          </button>
        </div>
      )}
      <div className="flex min-h-0 min-w-0 w-full flex-1 overflow-hidden">
        <div className="w-0 min-w-0 overflow-visible md:w-auto md:flex-shrink-0 md:h-full">
          <Sidebar />
        </div>
        <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
          <ToastProvider>
            <ConfirmProvider>
              <PageTransitionProvider>{children}</PageTransitionProvider>
            </ConfirmProvider>
          </ToastProvider>
        </div>
      </div>
    </div>
  );
}
